/**
 * The two write sequences behind the Log touch and Draft email buttons.
 *
 * WHY THESE LIVE OUTSIDE THE COMPONENT
 * ------------------------------------
 * The ORDER of these writes is the most load-bearing thing in the feature,
 * and while it sat inside a React click handler nothing could check it. The
 * `marketing` vitest project globs plain `.ts`, so pulling the sequence out
 * here makes the rule testable without a browser -- the same reasoning the
 * vitest config already applies to money-safety tests: a rule that guards
 * data should not depend on a browser download.
 *
 * TouchButtons.tsx imports these. It does not keep its own copy: a test that
 * exercises a duplicate of the logic proves the duplicate is right and says
 * nothing about the code that actually runs.
 *
 * WHY THE STAMP COMES FIRST
 * -------------------------
 * `contacts.last_contacted_at` is derived from `engagements` by one DB
 * trigger and is never written directly. If Draft email opened the mail
 * client without writing the engagement, a lead you emailed a minute ago
 * would still appear in `contacts_stalled` -- you would learn the view lies,
 * and the Zoho failure would have reproduced itself inside a nicer button.
 *
 * So the engagement is written BEFORE the handoff, and if that write fails
 * the mail client never opens. Opening it anyway would be worse than doing
 * nothing: the message goes out believing it was recorded, and the record
 * says the person is still waiting.
 */

import type { DataProvider } from "ra-core";
import type { Contact, Task } from "../types";

/**
 * react-admin's own DataProvider, rather than a hand-written structural type.
 *
 * A narrower type looked tidier and did not compile: DataProvider.update
 * requires {id, data, previousData}, so a parameter typed as a plain record
 * is WIDER, and contravariance makes the real provider unassignable. Using
 * their type means this cannot drift from what the app actually passes.
 * Tests supply a minimal fake and cast it, which keeps the fake honest about
 * being a fake.
 */
export type TouchDataProvider = DataProvider;

export type TouchKind = "call" | "email" | "met" | "other";

/** Today as YYYY-MM-DD in the browser's own timezone. */
export function todayLocal(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * A chosen date becomes a timestamp — TODAY keeps the clock, an earlier day
 * gets local noon.
 *
 * WHY TODAY IS A SEPARATE CASE
 * ----------------------------
 * The first version sent every date to noon, so logging a call at 3pm today
 * stored 12:00 and the history read it back as "today at 12:00 PM" — a
 * plainly wrong time for something the user had just done. Worse than
 * cosmetic: engagements sort by occurred_at, so a touch logged this afternoon
 * would file itself BEFORE a delivery receipt from this morning, and
 * `last_contacted_at` (max of the same column) would report noon for contact
 * that happened hours later.
 *
 * When the date is today we already know the real time, so we use it. There is
 * nothing to reconstruct.
 *
 * WHY AN EARLIER DAY STILL GETS NOON
 * ----------------------------------
 * For a back-dated touch the time is genuinely unknown — the user picked a
 * day, not a moment — and the only job left is to survive timezone conversion.
 * Local midnight sits on the day boundary, so converting it to UTC can push
 * the stored date a day either way, which is the off-by-one that makes
 * "logged as Tuesday" read back as Monday. Noon is twelve hours from either
 * edge and survives every zone.
 *
 * `now` is injected so both branches are testable without mocking the clock.
 */
export function dateOnlyToTimestamp(
  dateStr: string,
  now: Date = new Date(),
): string {
  if (dateStr === todayLocal(now)) return now.toISOString();
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0).toISOString();
}

/**
 * The resources a logged touch changes, and therefore what must be refetched.
 *
 * Writing through `useDataProvider()` bypasses react-admin's mutation hooks,
 * so nothing invalidates the react-query cache on its own. The symptom is
 * exactly what a user reported: the success toast appears, the row is in the
 * database, and the touch history on screen does not change until a reload.
 *
 * What moves:
 *   engagements — the row just written, and the list that displays it
 *   tasks       — a draft opened FROM a task closes that task. A touch logged
 *                 from the contact page closes nothing (see closeTask), so
 *                 this one is a no-op on that path. Kept in the list because
 *                 the list is shared and an invalidation of something that did
 *                 not change costs a refetch; the reverse costs a lie on
 *                 screen.
 *   contacts    — a trigger recomputes last_contacted_at
 *
 * Kept here rather than inline because both dialogs need the same list, and a
 * write that invalidates two of the three is the same stale-screen bug wearing
 * a smaller hat.
 */
export const TOUCH_INVALIDATES = ["engagements", "tasks", "contacts"] as const;

/** The slice of react-query's client this needs. Narrow, so a test can fake it. */
export type Invalidator = {
  invalidateQueries: (filters: { queryKey: unknown[] }) => unknown;
};

/**
 * Ask react-query to refetch everything a logged touch changed.
 *
 * A FUNCTION RATHER THAN THREE LINES IN A CLICK HANDLER, because the browser
 * test could not prove those three lines existed. Removing them entirely still
 * passed: Playwright's clicks fire window focus events, and react-query
 * refetches on focus by default, so the list refreshed for a reason that has
 * nothing to do with our code and does not happen to a real user sitting still
 * in a dialog. The end-to-end test cannot tell the two apart -- this one can.
 */
export function invalidateTouched(client: Invalidator): Promise<unknown> {
  return Promise.all(
    TOUCH_INVALIDATES.map((resource) =>
      client.invalidateQueries({ queryKey: [resource] }),
    ),
  );
}

/** The two columns closing a task needs. A record, so `previousData` is real. */
export type ClosableTask = Pick<Task, "id"> & { done_date?: string | null };

/**
 * Close EXACTLY the task the action came from, or nothing at all.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * IT USED TO GUESS, AND THE GUESS ASSERTED WORK NOBODY HAD DONE (crm#13).
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The previous version of this function took a CONTACT id and closed whichever
 * of that contact's open tasks had the earliest `due_date`. Both callers
 * invoked it unconditionally, and neither knew or asked which task the user
 * was working. So a hand-made reminder, an unrelated sequence step and the
 * task actually in front of the user were all equally eligible.
 *
 * That is not merely a lost task. `advance_enrollments()` materializes a
 * sequence step into a task, and once that task exists it is the only record
 * that the step's work is outstanding. Closing it fires
 * `private.task_done_engagement()`, which writes an `engagements` row of kind
 * `task` — while the `enrollment_steps` row stays `generated` and the
 * enrollment moves on. Nothing writes back the other way. So logging a phone
 * call could tick off step 3's EMAIL task, the email would never be sent, and
 * the record would positively assert that it had been. The system reporting
 * success for work that silently produced nothing is the exact Zoho failure
 * this project exists to prevent, reproduced inside it.
 *
 * SO THERE IS NO CONTACT-SCOPED SELECTION RULE ANY MORE, not a narrower one.
 * A better guess is still a guess, and a wrong close is worse than no close
 * because it is invisible. The caller passes the task record it already has,
 * or it passes nothing and nothing is closed. `null`/`undefined` is the
 * ordinary case, not an error: a touch logged from a contact page is not a
 * claim about any particular task.
 *
 * ALREADY-DONE TASKS ARE LEFT ALONE. Re-closing one would overwrite the date
 * it was actually finished on, and `done_date` is what the accountability
 * views read.
 *
 * Returns whether it wrote, so a caller can say so and a test can tell the
 * two refusals apart from a silent success.
 */
export async function closeTask(
  dataProvider: TouchDataProvider,
  task: ClosableTask | null | undefined,
): Promise<boolean> {
  if (!task) return false;
  if (task.done_date != null) return false;
  await dataProvider.update("tasks", {
    id: task.id,
    data: { done_date: new Date().toISOString() },
    previousData: task,
  });
  return true;
}

/**
 * Record contact that already happened, on the date it happened.
 *
 * `occurred_at` is the date the user chose, never now(): logging Tuesday's
 * call on Thursday must stamp Tuesday, or the cadence clock -- also derived
 * from occurred_at -- starts lying about how recently someone was reached.
 *
 * IT CLOSES NO TASK, AND THAT IS THE FIX RATHER THAN AN OMISSION (crm#13).
 * This is reached from the contact page, where "the task you were working on"
 * does not exist -- there is no task in the click, so there is nothing
 * truthful to close. It used to close the contact's oldest open one, which
 * meant typing a call note could tick off an unrelated sequence step and
 * record that the step's work was done. Logging a touch is now exactly what
 * its name says.
 *
 * The convenience it removes is real: a touch no longer ticks anything off by
 * itself. Closing a task is a claim about a particular task, so it belongs to
 * a control that knows which one -- see the Draft email item in the task's own
 * menu (marketing/TaskDraftEmail.tsx).
 */
export async function logTouch(
  dataProvider: TouchDataProvider,
  {
    contactId,
    kind,
    note,
    date,
  }: {
    contactId: Contact["id"];
    kind: TouchKind;
    note?: string;
    /** YYYY-MM-DD, back-dateable. */
    date: string;
  },
): Promise<void> {
  await dataProvider.create("engagements", {
    data: {
      contact_id: contactId,
      kind,
      source: "manual",
      occurred_at: dateOnlyToTimestamp(date),
      note: note?.trim() || null,
    },
  });
}

/**
 * Stamp the record, then hand off to the mail client.
 *
 * `openUrl` is injected rather than calling window.location directly, so the
 * ORDER of the stamp and the handoff is observable to a test. That order is
 * the whole feature: see the note at the top of this file.
 *
 * `task` IS THE TASK THIS DRAFT CAME FROM, AND THE ONLY ONE IT MAY CLOSE.
 * Opened from a task's own menu it is that task's record; opened from the
 * contact page it is absent, and then nothing is closed. See closeTask.
 *
 * Throws if the stamp fails, and in that case openUrl is never called.
 */
export async function draftEmailAndStamp(
  dataProvider: TouchDataProvider,
  {
    contactId,
    templateId,
    templateName,
    url,
    openUrl,
    task,
  }: {
    contactId: Contact["id"];
    templateId: number | string;
    templateName: string;
    /** A mailto: URL from buildMailto. Null when the contact has no email. */
    url: string | null;
    openUrl: (url: string) => void;
    /** The task this draft was opened from, if it was opened from one. */
    task?: ClosableTask | null;
  },
): Promise<void> {
  // No address is not an error to recover from here -- the button that calls
  // this is already disabled, with the reason on display.
  if (!url) throw new Error("This contact has no email address on file");

  await dataProvider.create("engagements", {
    data: {
      contact_id: contactId,
      kind: "email",
      source: "manual",
      content_id: templateId,
      note: `Drafted "${templateName}"`,
    },
  });
  await closeTask(dataProvider, task);

  // Only now. A failed stamp must not produce a sent email.
  openUrl(url);
}
